'use client'

import {
  type ApplySurveyVariant,
  ApplySurveyVariantValidator,
} from '@app/p/apply-survey/[token]/conditions/[step]/ApplySurveyVariantValidator'
import getComorbidityMedicationsVariantAction from '@app/p/apply-survey/[token]/conditions/[step]/getComorbidityMedicationsVariantAction'
import { ConditionAttributes } from '@components/hooks/PatientTrialApplySurveyProvider/SurveyQuestionsConditions'
import { isMobileScreen } from '@components/hooks/useIsMobileScreen'
import { Icon } from '@components/primitives/Icon'
import { Text } from '@components/primitives/Text'
import { faCapsules } from '@fortawesome/pro-regular-svg-icons/faCapsules'
import { faSpinnerThird } from '@fortawesome/pro-regular-svg-icons/faSpinnerThird'
import { NO_CONDITIONS_VALUE } from '@lib/questionnaire/trialApplication/generatePrescreeningQuestionConfig'
import { trackEvent } from '@lib/tracking'
import { TrackingEvents } from '@lib/tracking/TrackingEvents'
import uniq from '@lib/utilities/uniq'
import { isNotEmptyOrSkippedAnswer } from '@modules/patientApplyProfile/helpers/PatientApplyProfileData'
import { useSearchParams } from 'next/navigation'
import { type Dispatch, type SetStateAction, useEffect, useState } from 'react'
import ApplySelectOption from './ApplySelectOption'
import type { PopularMedication } from './CollectCurrentMedications.client'
import { PopularItems } from './PopularItems.client'
import { PopularPsychiatricMedications } from './popularMedications'
import { SearchInput } from './SearchInput.client'
import { SelectedItems } from './SelectedItems.client'
import type { ConditionCollectElementProps } from './types'

const TRACKING_TYPE = 'currentConditionSpecificMedication'

export default function CollectConditionSpecificMedications({
  patientProfileData,
  treatmentsByFrequency,
  trialApplicationIdToken,
}: ConditionCollectElementProps) {
  const searchParams = useSearchParams()
  const maybeVariant = searchParams?.get('variant')

  const parseResult = ApplySurveyVariantValidator.safeParse(maybeVariant)
  const queryParamVariant = parseResult.success ? parseResult.data : undefined

  const [variant, setVariant] = useState<ApplySurveyVariant | undefined>(
    queryParamVariant,
  )

  useEffect(() => {
    if (variant) return

    getComorbidityMedicationsVariantAction({
      applyIdToken: trialApplicationIdToken,
      patientIsOnMobile: isMobileScreen(),
    }).then((v) => {
      setVariant(v)
      trackEvent(TrackingEvents.APPLY_FLOW_UI_VARIANT_VIEWED, {
        trialApplicationIdToken,
        type: TRACKING_TYPE,
        variant: v,
      })
    })

    return () => {
      trackEvent(TrackingEvents.APPLY_FLOW_UI_VARIANT_COMPLETED, {
        trialApplicationIdToken,
        type: TRACKING_TYPE,
        val: currentMedications.join(', '),
        variant,
      })
    }
  }, [])

  const conditionToShowMedicationsFor = patientProfileData.conditions.filter(
    (condition) => condition !== NO_CONDITIONS_VALUE,
  )[0]

  // TO DO: in the future if we are fetching from our DB to get concepts by frequency then
  // we should probably update to use conceptId as key
  const mostPopularMedicationNames = PopularPsychiatricMedications.map(
    (medication, index) => ({
      displayName: medication.displayName,
      frequency: 1,
      id: index,
      name: medication.value,
    }),
  )

  const [currentMedications, setCurrentMedications] = useState(
    patientProfileData.currentConditionSpecificMedications.filter(
      isNotEmptyOrSkippedAnswer,
    ),
  )

  switch (variant) {
    case 'desktop-1':
    case 'mobile-1':
      return (
        <CollectConditionSpecificMedicationsInputVariant1
          conditionToShowMedicationsFor={conditionToShowMedicationsFor}
          currentMedications={currentMedications}
          mostPopularMedicationNames={mostPopularMedicationNames}
          setCurrentMedications={setCurrentMedications}
          treatmentsByFrequency={treatmentsByFrequency}
        />
      )
    case 'desktop-2':
      return (
        <CollectConditionSpecificMedicationsInputVariant2
          conditionToShowMedicationsFor={conditionToShowMedicationsFor}
          currentMedications={currentMedications}
          mostPopularMedicationNames={mostPopularMedicationNames}
          setCurrentMedications={setCurrentMedications}
          treatmentsByFrequency={treatmentsByFrequency}
        />
      )
    case 'mobile-2':
      return (
        <CollectConditionSpecificMedicationsInputVariant3
          conditionToShowMedicationsFor={conditionToShowMedicationsFor}
          currentMedications={currentMedications}
          mostPopularMedicationNames={mostPopularMedicationNames}
          setCurrentMedications={setCurrentMedications}
          treatmentsByFrequency={treatmentsByFrequency}
        />
      )
    case 'control-desktop':
    case 'control-mobile':
    case 'control':
      return (
        <CollectConditionSpecificMedicationsInput
          conditionToShowMedicationsFor={conditionToShowMedicationsFor}
          currentMedications={currentMedications}
          mostPopularMedicationNames={mostPopularMedicationNames}
          setCurrentMedications={setCurrentMedications}
          treatmentsByFrequency={treatmentsByFrequency}
        />
      )
    default:
      return (
        <div className='flex size-full items-center justify-center'>
          <Icon
            className='animate-spin text-primary600'
            icon={faSpinnerThird}
            size='2x'
          />
        </div>
      )
  }
}

function CollectConditionSpecificMedicationsInput({
  conditionToShowMedicationsFor,
  currentMedications,
  mostPopularMedicationNames,
  setCurrentMedications,
  treatmentsByFrequency,
}: {
  conditionToShowMedicationsFor?: string
  currentMedications: string[]
  mostPopularMedicationNames: PopularMedication[]
  setCurrentMedications: Dispatch<SetStateAction<string[]>>
  treatmentsByFrequency: ConditionCollectElementProps['treatmentsByFrequency']
}) {
  return (
    <div className='flex w-full flex-col gap-2'>
      <Text
        className='text-neutral900'
        element='p'
        styleName='h3'
        value={`Which medication(s) are you taking for ${conditionToShowMedicationsFor ? conditionToShowMedicationsFor : 'your condition'}?`}
      />
      <Text
        className='text-neutral900'
        element='p'
        styleName='p-small'
        value='Add all of your medications to get the most accurate study matches.'
      />

      <SearchInput
        itemsByFrequencyCollection={treatmentsByFrequency}
        menuPlacement='bottom'
        onChange={(newValue) =>
          setCurrentMedications((prev) => uniq([...prev, newValue]))
        }
        placeholder='Search for Other Medications'
        searchTitle=''
        trackingType={TRACKING_TYPE}
      />

      <SelectedItems
        icon={faCapsules}
        onChange={setCurrentMedications}
        title='Selected Medications'
        trackingType={TRACKING_TYPE}
        value={currentMedications}
      />

      {currentMedications.filter(Boolean).map((currentMedication) => (
        <input
          key={currentMedication}
          name={ConditionAttributes.currentConditionSpecificMedications}
          type='hidden'
          value={currentMedication}
        />
      ))}

      {mostPopularMedicationNames
        .filter((medication) => !currentMedications.includes(medication.name))
        .map((unselectedMedication) => (
          <input
            key={unselectedMedication.name}
            name={ConditionAttributes.unselectedConditionSpecificMedications}
            type='hidden'
            value={unselectedMedication.name}
          />
        ))}

      <PopularItems
        addNoneOption
        itemsByFrequency={mostPopularMedicationNames}
        onChange={setCurrentMedications}
        title='Common Related Medications'
        trackingType={TRACKING_TYPE}
        value={currentMedications}
      />
    </div>
  )
}

function CollectConditionSpecificMedicationsInputVariant1({
  conditionToShowMedicationsFor,
  currentMedications,
  mostPopularMedicationNames,
  setCurrentMedications,
  treatmentsByFrequency,
}: {
  conditionToShowMedicationsFor?: string
  currentMedications: string[]
  mostPopularMedicationNames: PopularMedication[]
  setCurrentMedications: Dispatch<SetStateAction<string[]>>
  treatmentsByFrequency: ConditionCollectElementProps['treatmentsByFrequency']
}) {
  const [popularMedicationNames, setPopularMedicationNames] = useState<
    PopularMedication[]
  >(mostPopularMedicationNames)

  return (
    <div className='flex w-full flex-col gap-2'>
      <Text
        className='text-neutral900'
        element='p'
        styleName='h3'
        value={`Which medication(s) are you taking for ${conditionToShowMedicationsFor ? conditionToShowMedicationsFor : 'your condition'}?`}
      />
      <Text
        className='text-neutral900'
        element='p'
        styleName='p-small'
        value='Add all of your medications to get the most accurate study matches.'
      />

      <SearchInput
        itemsByFrequencyCollection={treatmentsByFrequency}
        menuPlacement='bottom'
        onChange={(newValue) => {
          setCurrentMedications((prev) => uniq([...prev, newValue]))
          if (!popularMedicationNames.find((m) => m.name === newValue)) {
            setPopularMedicationNames((prev) => [
              {
                displayName: newValue,
                frequency: 1,
                id: Date.now(), // not used in this context
                name: newValue,
              },
              ...prev,
            ])
          }
        }}
        placeholder='Search for Other Medications'
        searchTitle=''
        trackingType={TRACKING_TYPE}
      />

      <div className='mt-4 space-y-4'>
        {popularMedicationNames.map((medication) => {
          const selected = currentMedications.includes(medication.name)

          return (
            <ApplySelectOption
              className='h-[72px]'
              key={medication.name}
              label={medication.displayName}
              onClick={() =>
                setCurrentMedications((prev) =>
                  prev.includes(medication.name)
                    ? prev.filter((m) => m !== medication.name)
                    : [...prev, medication.name],
                )
              }
              selected={selected}
              value={medication.name}
            />
          )
        })}
      </div>

      {currentMedications.filter(Boolean).map((currentMedication) => (
        <input
          key={currentMedication}
          name={ConditionAttributes.currentConditionSpecificMedications}
          type='hidden'
          value={currentMedication}
        />
      ))}

      {popularMedicationNames
        .filter((medication) => !currentMedications.includes(medication.name))
        .map((unselectedMedication) => (
          <input
            key={unselectedMedication.name}
            name={ConditionAttributes.unselectedConditionSpecificMedications}
            type='hidden'
            value={unselectedMedication.name}
          />
        ))}
    </div>
  )
}

function CollectConditionSpecificMedicationsInputVariant2({
  conditionToShowMedicationsFor,
  currentMedications,
  mostPopularMedicationNames,
  setCurrentMedications,
  treatmentsByFrequency,
}: {
  conditionToShowMedicationsFor?: string
  currentMedications: string[]
  mostPopularMedicationNames: PopularMedication[]
  setCurrentMedications: Dispatch<SetStateAction<string[]>>
  treatmentsByFrequency: ConditionCollectElementProps['treatmentsByFrequency']
}) {
  const [popularMedicationNames, setPopularMedicationNames] = useState<
    PopularMedication[]
  >(mostPopularMedicationNames)

  return (
    <div className='flex w-full flex-col gap-2'>
      <Text
        className='text-neutral900'
        element='p'
        styleName='h3'
        value={`Which medication(s) are you taking for ${conditionToShowMedicationsFor ? conditionToShowMedicationsFor : 'your condition'}?`}
      />
      <Text
        className='text-neutral900'
        element='p'
        styleName='p-small'
        value='Add all of your medications to get the most accurate study matches.'
      />

      <SearchInput
        itemsByFrequencyCollection={treatmentsByFrequency}
        menuPlacement='bottom'
        onChange={(newValue) => {
          setCurrentMedications((prev) => uniq([...prev, newValue]))
          if (!popularMedicationNames.find((m) => m.name === newValue)) {
            setPopularMedicationNames((prev) => [
              {
                displayName: newValue,
                frequency: 1,
                id: Date.now(), // not used in this context
                name: newValue,
              },
              ...prev,
            ])
          }
        }}
        placeholder='Search for Other Medications'
        searchTitle=''
        trackingType={TRACKING_TYPE}
      />

      <div className='mt-4 grid grid-cols-2 gap-4'>
        {mostPopularMedicationNames.map((medication) => {
          const selected = currentMedications.includes(medication.name)

          return (
            <ApplySelectOption
              className='h-[72px]'
              key={medication.name}
              label={medication.displayName}
              onClick={() =>
                setCurrentMedications((prev) =>
                  prev.includes(medication.name)
                    ? prev.filter((m) => m !== medication.name)
                    : [...prev, medication.name],
                )
              }
              selected={selected}
              value={medication.name}
            />
          )
        })}
      </div>

      {currentMedications.filter(Boolean).map((currentMedication) => (
        <input
          key={currentMedication}
          name={ConditionAttributes.currentConditionSpecificMedications}
          type='hidden'
          value={currentMedication}
        />
      ))}

      {popularMedicationNames
        .filter((medication) => !currentMedications.includes(medication.name))
        .map((unselectedMedication) => (
          <input
            key={unselectedMedication.name}
            name={ConditionAttributes.unselectedConditionSpecificMedications}
            type='hidden'
            value={unselectedMedication.name}
          />
        ))}
    </div>
  )
}

function CollectConditionSpecificMedicationsInputVariant3({
  conditionToShowMedicationsFor,
  currentMedications,
  mostPopularMedicationNames,
  setCurrentMedications,
  treatmentsByFrequency,
}: {
  conditionToShowMedicationsFor?: string
  currentMedications: string[]
  mostPopularMedicationNames: PopularMedication[]
  setCurrentMedications: Dispatch<SetStateAction<string[]>>
  treatmentsByFrequency: ConditionCollectElementProps['treatmentsByFrequency']
}) {
  const [popularMedicationNames, setPopularMedicationNames] = useState<
    PopularMedication[]
  >(mostPopularMedicationNames)

  return (
    <div className='flex w-full flex-col gap-2'>
      <Text
        className='text-neutral900'
        element='p'
        styleName='h3'
        value={`Which medication(s) are you taking for ${conditionToShowMedicationsFor ? conditionToShowMedicationsFor : 'your condition'}?`}
      />
      <Text
        className='text-neutral900'
        element='p'
        styleName='p-small'
        value='Add all of your medications to get the most accurate study matches.'
      />

      <div className='mt-4 space-y-4'>
        {popularMedicationNames.map((medication) => {
          const selected = currentMedications.includes(medication.name)

          return (
            <ApplySelectOption
              className='h-[72px]'
              key={medication.name}
              label={medication.displayName}
              onClick={() =>
                setCurrentMedications((prev) =>
                  prev.includes(medication.name)
                    ? prev.filter((m) => m !== medication.name)
                    : [...prev, medication.name],
                )
              }
              selected={selected}
              value={medication.name}
            />
          )
        })}
      </div>

      <SearchInput
        itemsByFrequencyCollection={treatmentsByFrequency}
        menuPlacement='top'
        onChange={(newValue) => {
          setCurrentMedications((prev) => uniq([...prev, newValue]))
          if (!popularMedicationNames.find((m) => m.name === newValue)) {
            setPopularMedicationNames((prev) => [
              {
                displayName: newValue,
                frequency: 1,
                id: Date.now(), // not used in this context
                name: newValue,
              },
              ...prev,
            ])
          }
        }}
        placeholder='Search for Other Medications'
        searchTitle=''
        trackingType={TRACKING_TYPE}
      />

      {currentMedications.filter(Boolean).map((currentMedication) => (
        <input
          key={currentMedication}
          name={ConditionAttributes.currentConditionSpecificMedications}
          type='hidden'
          value={currentMedication}
        />
      ))}

      {popularMedicationNames
        .filter((medication) => !currentMedications.includes(medication.name))
        .map((unselectedMedication) => (
          <input
            key={unselectedMedication.name}
            name={ConditionAttributes.unselectedConditionSpecificMedications}
            type='hidden'
            value={unselectedMedication.name}
          />
        ))}
    </div>
  )
}
